/**
 * Mail-Werkstatt: links die Mail, rechts Kommentare, Änderungen und
 * Versionen — wie „Zusammenarbeiten" in Word oder Google Docs.
 *
 * Modi: Ansehen · Kommentieren (Text markieren) · Zeichnen (Stellen
 * einkreisen) · Text ändern (wird als Vorschlag gespeichert, das Team kann
 * auch direkt übernehmen) · Abschnitt entfernen (ganze Absätze, Kästen oder
 * Zeilen samt Gestaltung — ebenso) · Änderungen (gestrichen/eingefügt).
 * Die Werkstatt kennt keine Abrufe — alles läuft über den Adapter
 * (Team-Seite oder Feedback-Link). Fehlt `adapter.senden`, ist sie nur zum
 * Lesen da: Ansehen, Änderungen und Versionen, aber nichts Neues schreiben.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Check, Eye, GitCompare, History, ImagePlus, Info, Mail, MessageSquarePlus, Monitor, PenTool, Smartphone, Terminal, Trash2, Type,
} from 'lucide-react';
import { useToast } from '../Toast';
import { datumZeit, istKonflikt, type Anker, type Markierung, type PlanungsDetail, type VersionVoll, type WerkstattAdapter } from '../../lib/planung';
import { aenderungenAnwenden, type Aenderung } from '../../lib/zusammenarbeit';
import { MailBuehne, type AnkerWahl, type BuehnenEintrag, type BuehnenHandle, type BuehnenVorschlag } from './MailBuehne';
import { KommentarSpalte } from './KommentarSpalte';
import { AenderungenListe } from './AenderungKarte';
import { VersionenPanel } from './VersionenPanel';
import { BildTauschen } from './BildTauschen';
import { ClaudePromptDialog } from './ClaudePromptDialog';
import { Rueckfrage } from './Teile';
import {
  BREIT_AB, KONFLIKT_TEXT, abschnittText, eintragOffen, fehlerText, istVersendet, stellenText, vorschlagOffen,
  type Breite, type BuehnenModus, type Entwurf, type WerkstattAktionen,
} from './werkstattHilfen';
import './Zusammenarbeit.css';

type Seite = 'kommentare' | 'aenderungen' | 'versionen';

/** Abstand zwischen Mail und Spalte (px) — wie --za-luecke im CSS. */
const LUECKE = 28;
/** Notiz einer Version (mail_inhalt_aendern) — als Vorschlag dürfen es 4000 Zeichen sein. */
const NOTIZ_MAX = 500;
const LEER: ReadonlySet<string> = new Set();

const MODI: { id: BuehnenModus; label: string; Icon: typeof Eye; tipp: string }[] = [
  { id: 'ansehen', label: 'Ansehen', Icon: Eye, tipp: 'Die Mail so ansehen, wie sie ankommt' },
  { id: 'kommentieren', label: 'Kommentieren', Icon: MessageSquarePlus, tipp: 'Text markieren oder auf eine Stelle tippen und kommentieren' },
  { id: 'zeichnen', label: 'Zeichnen', Icon: PenTool, tipp: 'Mit dem Zeichenstift Stellen einkreisen' },
  { id: 'bearbeiten', label: 'Text ändern', Icon: Type, tipp: 'Formulierungen und Formatierung (fett, Farbe, Link, Emoji) direkt in der Mail ändern' },
  { id: 'entfernen', label: 'Entfernen', Icon: Trash2, tipp: 'Abschnitt entfernen: ganze Absätze, Kästen oder Zeilen samt Gestaltung aus der Mail nehmen' },
  { id: 'aenderungen', label: 'Änderungen', Icon: GitCompare, tipp: 'Änderungsvorschläge in der Mail zeigen' },
];

type Frage = {
  titel: string;
  text: React.ReactNode;
  ja: string;
  gefaehrlich?: boolean;
  onJa: () => void | Promise<void>;
  /** Zweiter Knopf mit eigener Wirkung (sonst nur schließen) */
  nein?: string;
  onNein?: () => void | Promise<void>;
};

type Props = {
  mail: PlanungsDetail;
  adapter: WerkstattAdapter;
  /** Kopfbereich über der Werkzeugleiste (Titel, Termine, Knöpfe der Seite) */
  kopf?: React.ReactNode;
};

export function MailWerkstatt({ mail, adapter, kopf }: Props) {
  const toast = useToast();
  const idBasis = useId();
  const wurzelRef = useRef<HTMLDivElement>(null);
  const buehneRef = useRef<BuehnenHandle>(null);
  const bezugRef = useRef<HTMLDivElement>(null);
  const vorherModus = useRef<BuehnenModus>('ansehen');

  const [breit, setBreit] = useState(true);
  const [modus, setModus] = useState<BuehnenModus>('ansehen');
  const [breite, setBreite] = useState<Breite>(() => (typeof window !== 'undefined' && window.innerWidth < 640 ? 'handy' : 'computer'));
  const [seite, setSeite] = useState<Seite>('kommentare');
  const [aktivId, setAktivId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [positionen, setPositionen] = useState<Map<string, number>>(() => new Map());
  const [fokus, setFokus] = useState(0);
  const [bearbeitet, setBearbeitet] = useState(0);
  const [striche, setStriche] = useState(0);
  const [abschnitte, setAbschnitte] = useState(0);
  const [betreff, setBetreff] = useState(mail.betreff);
  const [notiz, setNotiz] = useState('');
  const [speichert, setSpeichert] = useState<null | 'vorschlag' | 'direkt'>(null);
  const [version, setVersion] = useState<VersionVoll | null>(null);
  const [frage, setFrage] = useState<Frage | null>(null);
  const [frageLaeuft, setFrageLaeuft] = useState(false);
  const [kommentarText, setKommentarText] = useState('');
  const [bildTausch, setBildTausch] = useState(false);
  const [claudeOffen, setClaudeOffen] = useState(false);
  const [ohneOrt, setOhneOrt] = useState<ReadonlySet<string>>(() => new Set());

  // Andere Mail → frisch anfangen
  const mailId = useRef(mail.id);
  useEffect(() => {
    if (mailId.current === mail.id) return;
    mailId.current = mail.id;
    setModus('ansehen');
    setAktivId(null);
    setHoverId(null);
    setEntwurf(null);
    setVersion(null);
    setBearbeitet(0);
    setStriche(0);
    setAbschnitte(0);
    setPositionen(new Map());
    setKommentarText('');
  }, [mail.id]);

  // Zweispaltig erst ab BREIT_AB (Breite der Werkstatt, nicht des Fensters)
  useLayoutEffect(() => {
    const el = wurzelRef.current;
    if (!el) return;
    setBreit(el.clientWidth >= BREIT_AB);
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(() => setBreit(el.clientWidth >= BREIT_AB));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Abgeleitet ───────────────────────────────────────────────────────────

  const oberste = useMemo(() => {
    const ids = new Set(mail.feedback.map((f) => f.id));
    return mail.feedback.filter((f) => !f.antwort_auf || !ids.has(f.antwort_auf));
  }, [mail.feedback]);
  const buehnenEintraege = useMemo<BuehnenEintrag[]>(() => oberste
    .filter((f) => f.art !== 'vorschlag')
    .map((f) => ({ id: f.id, anker: f.anker, zitat: f.zitat, markierung: f.markierung, zeigen: eintragOffen(f) })), [oberste]);
  const buehnenVorschlaege = useMemo<BuehnenVorschlag[]>(() => oberste
    .filter((f) => f.art === 'vorschlag' && (f.aenderungen?.length ?? 0) > 0)
    .map((f) => ({ id: f.id, aenderungen: f.aenderungen ?? [], offen: vorschlagOffen(f) })), [oberste]);
  const zahlOffen = oberste.filter(eintragOffen).length;
  const zahlVorschlaege = oberste.filter(vorschlagOffen).length;

  const versendet = istVersendet(mail);
  const darfSchreiben = !!adapter.senden;
  const modi = darfSchreiben ? MODI : MODI.filter((m) => m.id === 'ansehen' || m.id === 'aenderungen');
  const darfDirekt = adapter.darfEntscheiden && !!adapter.inhaltSpeichern && !mail.aus_assistent && !versendet;
  const betreffGeaendert = modus === 'bearbeiten' && betreff.trim() !== '' && betreff.trim() !== mail.betreff.trim();
  const hatAenderung = bearbeitet > 0 || betreffGeaendert;
  const abschnittOffen = modus === 'entfernen' && abschnitte > 0;
  const ungespeichert = (modus === 'bearbeiten' && hatAenderung) || (modus === 'zeichnen' && striche > 0) || abschnittOffen;
  // Text oder Auswahl, die beim Neuladen der Mail verloren gingen
  const textUngespeichert = (modus === 'bearbeiten' && hatAenderung && !version) || (abschnittOffen && !version);
  const notizZuLang = darfDirekt && notiz.trim().length > NOTIZ_MAX;

  // ── Modi ─────────────────────────────────────────────────────────────────

  const modusSetzen = useCallback((neu: BuehnenModus) => {
    setModus((alt) => {
      if (alt === 'ansehen' || alt === 'kommentieren' || alt === 'aenderungen') vorherModus.current = alt;
      return neu;
    });
    if (neu === 'bearbeiten') {
      setBetreff(mail.betreff);
      setNotiz('');
    }
    if (neu === 'entfernen') setNotiz('');
    setBearbeitet(0);
    setStriche(0);
    setAbschnitte(0);
    setVersion(null);
  }, [mail.betreff]);

  const modusWechseln = (neu: BuehnenModus) => {
    if (neu === modus && !version) return;
    if (ungespeichert) {
      setFrage({
        titel: modus === 'bearbeiten' ? 'Änderungen verwerfen?' : modus === 'entfernen' ? 'Auswahl verwerfen?' : 'Zeichnung verwerfen?',
        text: modus === 'bearbeiten'
          ? 'Du hast Text geändert, aber noch nicht gespeichert. Wenn Du jetzt wechselst, ist die Änderung weg.'
          : modus === 'entfernen'
            ? 'Du hast Abschnitte markiert, aber noch nicht gespeichert. Wenn Du jetzt wechselst, ist die Auswahl weg.'
            : 'Deine Zeichnung ist noch nicht gespeichert. Wenn Du jetzt wechselst, ist sie weg.',
        ja: 'Verwerfen',
        gefaehrlich: true,
        onJa: () => modusSetzen(neu),
      });
      return;
    }
    modusSetzen(neu);
  };

  // Schreiben nicht (mehr) erlaubt (z. B. abgemeldet): zurück zum Ansehen, Entwurf weg
  useEffect(() => {
    if (darfSchreiben) return;
    vorherModus.current = 'ansehen';
    setModus((m) => (m === 'kommentieren' || m === 'zeichnen' || m === 'bearbeiten' || m === 'entfernen' ? 'ansehen' : m));
    setEntwurf(null);
    setBearbeitet(0);
    setStriche(0);
    setAbschnitte(0);
  }, [darfSchreiben]);

  const verlassen = () => {
    if (modus === 'bearbeiten' || modus === 'zeichnen' || modus === 'entfernen') modusWechseln(vorherModus.current);
    else if (version) setVersion(null);
    else if (entwurf) setEntwurf(null);
    else if (aktivId) setAktivId(null);
  };
  const verlassenRef = useRef(verlassen);
  useLayoutEffect(() => { verlassenRef.current = verlassen; });

  // Esc verlässt Bearbeiten, Entfernen und Zeichnen (nicht, solange ein Dialog offen ist)
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('.modal-backdrop')) return;
      verlassenRef.current();
    };
    document.addEventListener('keydown', taste);
    return () => document.removeEventListener('keydown', taste);
  }, []);

  // Nicht gespeicherte Änderungen: beim Verlassen der Seite warnen
  useEffect(() => {
    if (!ungespeichert) return;
    const warnen = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
  }, [ungespeichert]);

  // ── Aktionen über den Adapter ────────────────────────────────────────────

  // Annehmen, Ablehnen, Wiederherstellen laden die Mail neu — ungespeicherte Textänderungen wären dann weg
  const erstFragenRef = useRef<(weiter: () => void) => void>(() => {});
  useLayoutEffect(() => {
    erstFragenRef.current = (weiter) => {
      if (!textUngespeichert) { weiter(); return; }
      setFrage({
        titel: 'Änderungen verwerfen?',
        text: 'Deine ungespeicherten Änderungen gehen verloren, weil die Mail danach neu geladen wird. Willst Du sie behalten, speichere sie vorher als Vorschlag.',
        ja: 'Verwerfen und weiter',
        gefaehrlich: true,
        onJa: () => { modusSetzen(vorherModus.current); weiter(); },
      });
    };
  });

  const aktionen = useMemo<WerkstattAktionen>(() => ({
    erstFragen: (weiter) => erstFragenRef.current(weiter),
    senden: async (daten, erfolg) => {
      if (!adapter.senden) return null;
      try {
        const id = await adapter.senden(daten);
        toast.success(erfolg);
        await adapter.neuLaden();
        return id;
      } catch (e) {
        toast.error(fehlerText(e));
        return null;
      }
    },
    erledigen: async (f, wieder) => {
      if (!adapter.entscheiden) return;
      try {
        await adapter.entscheiden(f.id, wieder ? 'offen' : 'erledigt');
        toast.success(wieder ? 'Wieder geöffnet.' : 'Als erledigt markiert.');
        await adapter.neuLaden();
      } catch (e) {
        toast.error(fehlerText(e));
      }
    },
    zurueckziehen: (f) => {
      if (!adapter.zurueckziehen) return;
      setFrage({
        titel: 'Zurückziehen?',
        text: f.art === 'vorschlag'
          ? 'Dein Vorschlag wird gelöscht. Das geht, solange das Team noch nicht entschieden hat.'
          : f.art === 'passt'
            ? 'Dein „Passt so“ wird zurückgenommen.'
            : 'Dein Kommentar wird gelöscht. Das geht, solange er noch offen ist.',
        ja: 'Zurückziehen',
        gefaehrlich: true,
        onJa: async () => {
          try {
            await adapter.zurueckziehen!(f.id);
            toast.success('Zurückgezogen.');
            setAktivId((a) => (a === f.id ? null : a));
            await adapter.neuLaden();
          } catch (e) {
            toast.error(fehlerText(e));
          }
        },
      });
    },
  }), [adapter, toast]);

  async function frageBestaetigen(ja: boolean) {
    if (!frage) return;
    const tun = ja ? frage.onJa : frage.onNein;
    if (!tun) { setFrage(null); return; }
    setFrageLaeuft(true);
    try {
      await tun();
    } finally {
      setFrageLaeuft(false);
      setFrage(null);
    }
  }

  // ── Bühne ────────────────────────────────────────────────────────────────

  const waehlen = useCallback((id: string | null) => {
    setAktivId(id);
    if (id) setSeite((s) => (s === 'versionen' ? 'kommentare' : s));
  }, []);

  const beimAnker = (w: AnkerWahl) => {
    setEntwurf({ anker: w.anker, zitat: w.zitat, markierung: null });
    setAktivId(null);
    setSeite('kommentare');
    setFokus((n) => n + 1);
  };

  const beimMarkieren = (m: Markierung, anker: Anker | null) => {
    setEntwurf({ anker, zitat: null, markierung: m });
    setAktivId(null);
    setSeite('kommentare');
    modusSetzen(vorherModus.current === 'aenderungen' ? 'ansehen' : vorherModus.current);
    setFokus((n) => n + 1);
  };

  const zeigen = (id: string) => {
    // nach einem Wechsel Handy/Computer erst umbrechen lassen
    window.setTimeout(() => buehneRef.current?.scrollZu(id), 260);
  };

  const versionAnsehen = (v: VersionVoll | null) => {
    if (v && ungespeichert) {
      setFrage({
        titel: 'Änderungen verwerfen?',
        text: 'Du hast etwas geändert, aber noch nicht gespeichert. Wenn Du jetzt eine Version ansiehst, ist es weg.',
        ja: 'Verwerfen',
        gefaehrlich: true,
        onJa: () => { modusSetzen('ansehen'); setVersion(v); },
      });
      return;
    }
    if (v && modus !== 'ansehen') modusSetzen('ansehen');
    setVersion(v);
  };

  // ── Bearbeiten speichern ─────────────────────────────────────────────────

  const verwerfen = () => {
    const entfernen = modus === 'entfernen';
    const weg = () => {
      if (entfernen) buehneRef.current?.abschnitteLeeren();
      else buehneRef.current?.zuruecksetzen();
      setBetreff(mail.betreff);
      setNotiz('');
      modusSetzen(vorherModus.current);
    };
    if (entfernen ? abschnitte === 0 : !hatAenderung) { weg(); return; }
    setFrage({
      titel: entfernen ? 'Auswahl verwerfen?' : 'Änderungen verwerfen?',
      text: entfernen ? 'Alle markierten Abschnitte werden wieder losgelassen.' : 'Alles, was Du im Text geändert hast, wird zurückgesetzt.',
      ja: 'Verwerfen',
      gefaehrlich: true,
      onJa: weg,
    });
  };

  /** `gemerkt`: Stand aus einem gescheiterten direkten Speichern (Basis bleibt die alte Fassung). */
  async function alsVorschlagSpeichern(gemerkt?: { aenderungen: Aenderung[]; betreff: string | null }) {
    const aenderungen = gemerkt?.aenderungen ?? buehneRef.current?.aenderungen() ?? [];
    const neuerBetreff = gemerkt ? gemerkt.betreff : betreffGeaendert ? betreff.trim() : null;
    if (!aenderungen.length && !neuerBetreff) { toast.info('Du hast noch nichts geändert.'); return; }
    if (aenderungen.length > 200) { toast.error('Das sind zu viele Stellen auf einmal — speichere bitte in mehreren Schritten.'); return; }
    if (!adapter.senden) return;
    setSpeichert('vorschlag');
    try {
      const id = await adapter.senden({
        art: 'vorschlag',
        text: notiz.trim() || null,
        aenderungen,
        betreff_vorschlag: neuerBetreff,
        basis_md5: mail.md5,
        basis_version: mail.version_nr,
      });
      toast.success(adapter.darfEntscheiden ? 'Vorschlag gespeichert.' : 'Dein Vorschlag ist gespeichert — das Team sieht ihn jetzt.');
      await adapter.neuLaden();
      modusSetzen('aenderungen');
      setAktivId(id);
    } catch (e) {
      toast.error(fehlerText(e));
    } finally {
      setSpeichert(null);
    }
  }

  async function direktUebernehmen() {
    if (!adapter.inhaltSpeichern || !darfDirekt) return;
    const aenderungen = buehneRef.current?.aenderungen() ?? [];
    const neuerBetreff = betreffGeaendert ? betreff.trim() : null;
    if (!aenderungen.length && !neuerBetreff) { toast.info('Du hast noch nichts geändert.'); return; }
    if (notizZuLang) {
      toast.error(`Für „Direkt übernehmen“ darf die Erklärung höchstens ${NOTIZ_MAX} Zeichen haben — bitte kürzen oder als Vorschlag speichern.`);
      return;
    }
    let html = mail.html;
    if (aenderungen.length) {
      const r = aenderungenAnwenden(mail.html, aenderungen);
      const nicht = r.ergebnisse.filter((e) => !e.ok).length;
      if (nicht) {
        toast.error(`${stellenText(nicht)} ${nicht === 1 ? 'ließ' : 'ließen'} sich nicht sauber übernehmen. Speichere die Änderung lieber als Vorschlag.`);
        return;
      }
      html = r.html;
    }
    setSpeichert('direkt');
    try {
      const r = await adapter.inhaltSpeichern({ html, basisMd5: mail.md5, quelle: 'bearbeitet', betreff: neuerBetreff, notiz: notiz.trim() || null });
      toast.success(`Gespeichert — die Mail ist jetzt Version ${r.version_nr}.`);
      setBearbeitet(0);
      await adapter.neuLaden();
      modusSetzen(vorherModus.current);
    } catch (e) {
      if (!istKonflikt(e)) toast.error(fehlerText(e));
      else {
        // Jemand war schneller: nichts verlieren — als Vorschlag sichern oder verwerfen und neu laden
        const gemerkt = { aenderungen, betreff: neuerBetreff };
        setFrage({
          titel: 'Die Mail wurde inzwischen geändert',
          text: (
            <>
              <p>Jemand hat die Mail gerade geändert, darum ließ sich Deine Änderung nicht direkt speichern.</p>
              <p>Speichere sie als Vorschlag — dann geht nichts verloren und Du kannst sie auf dem neuen Stand annehmen. Danach lade ich den neuen Stand.</p>
            </>
          ),
          ja: 'Als Vorschlag speichern',
          onJa: () => alsVorschlagSpeichern(gemerkt),
          nein: 'Verwerfen und neu laden',
          onNein: async () => {
            toast.info(KONFLIKT_TEXT);
            modusSetzen(vorherModus.current);
            await adapter.neuLaden();
          },
        });
      }
    } finally {
      setSpeichert(null);
    }
  }

  /** Markierte Abschnitte als Vorschlag ablegen (`gemerkt`: Stand aus einem gescheiterten direkten Entfernen). */
  async function entfernenAlsVorschlag(gemerkt?: Aenderung[]) {
    const aenderungen = gemerkt ?? buehneRef.current?.abschnitte() ?? [];
    if (!aenderungen.length) { toast.info('Du hast noch nichts markiert.'); return; }
    if (aenderungen.length > 100) { toast.error('Das sind zu viele Abschnitte auf einmal — speichere bitte in mehreren Schritten.'); return; }
    if (!adapter.senden) return;
    setSpeichert('vorschlag');
    try {
      const id = await adapter.senden({
        art: 'vorschlag',
        text: notiz.trim() || null,
        aenderungen,
        betreff_vorschlag: null,
        basis_md5: mail.md5,
        basis_version: mail.version_nr,
      });
      toast.success(adapter.darfEntscheiden ? 'Vorschlag gespeichert.' : 'Dein Vorschlag ist gespeichert — das Team sieht ihn jetzt.');
      await adapter.neuLaden();
      modusSetzen('aenderungen');
      setAktivId(id);
    } catch (e) {
      toast.error(fehlerText(e));
    } finally {
      setSpeichert(null);
    }
  }

  /** Markierte Abschnitte sofort aus der Mail nehmen (neue Version). */
  async function direktEntfernen() {
    if (!adapter.inhaltSpeichern || !darfDirekt) return;
    const aenderungen = buehneRef.current?.abschnitte() ?? [];
    if (!aenderungen.length) { toast.info('Du hast noch nichts markiert.'); return; }
    if (notizZuLang) {
      toast.error(`Für „Direkt entfernen“ darf die Erklärung höchstens ${NOTIZ_MAX} Zeichen haben — bitte kürzen oder als Vorschlag speichern.`);
      return;
    }
    const r = aenderungenAnwenden(mail.html, aenderungen);
    const nicht = r.ergebnisse.filter((e) => !e.ok).length;
    if (nicht) {
      toast.error(`${abschnittText(nicht)} ließ${nicht === 1 ? '' : 'en'} sich nicht sauber entfernen. Speichere die Auswahl lieber als Vorschlag.`);
      return;
    }
    setSpeichert('direkt');
    try {
      const res = await adapter.inhaltSpeichern({
        html: r.html,
        basisMd5: mail.md5,
        quelle: 'bearbeitet',
        notiz: notiz.trim() || (aenderungen.length === 1 ? 'Abschnitt entfernt' : `${aenderungen.length} Abschnitte entfernt`),
      });
      toast.success(`Entfernt — die Mail ist jetzt Version ${res.version_nr}.`);
      setAbschnitte(0);
      await adapter.neuLaden();
      modusSetzen(vorherModus.current);
    } catch (e) {
      if (!istKonflikt(e)) toast.error(fehlerText(e));
      else {
        setFrage({
          titel: 'Die Mail wurde inzwischen geändert',
          text: (
            <>
              <p>Jemand hat die Mail gerade geändert, darum ließ sich die Auswahl nicht direkt entfernen.</p>
              <p>Speichere sie als Vorschlag — dann geht nichts verloren und Du kannst sie auf dem neuen Stand annehmen. Danach lade ich den neuen Stand.</p>
            </>
          ),
          ja: 'Als Vorschlag speichern',
          onJa: () => entfernenAlsVorschlag(aenderungen),
          nein: 'Verwerfen und neu laden',
          onNein: async () => {
            toast.info(KONFLIKT_TEXT);
            modusSetzen(vorherModus.current);
            await adapter.neuLaden();
          },
        });
      }
    } finally {
      setSpeichert(null);
    }
  }

  /** Bild getauscht: neue Fassung direkt als Version speichern (wie „Direkt übernehmen“). */
  async function bildUebernehmen(html: string) {
    if (!adapter.inhaltSpeichern || !darfDirekt) return;
    try {
      const r = await adapter.inhaltSpeichern({ html, basisMd5: mail.md5, quelle: 'bearbeitet', notiz: 'Bild getauscht' });
      toast.success(`Bild getauscht — die Mail ist jetzt Version ${r.version_nr}.`);
      setBildTausch(false);
      await adapter.neuLaden();
    } catch (e) {
      if (istKonflikt(e)) {
        toast.info(KONFLIKT_TEXT);
        setBildTausch(false);
        await adapter.neuLaden();
        return;
      }
      throw new Error(fehlerText(e));
    }
  }

  // ── Seitenleiste ─────────────────────────────────────────────────────────

  const reiter: { id: Seite; label: string; zahl: number; titel: string }[] = [
    { id: 'kommentare', label: 'Kommentare', zahl: zahlOffen, titel: `${zahlOffen} offene Rückmeldungen` },
    { id: 'aenderungen', label: 'Änderungen', zahl: zahlVorschlaege, titel: `${zahlVorschlaege} offene Vorschläge` },
    { id: 'versionen', label: 'Versionen', zahl: mail.versionen.length, titel: `${mail.versionen.length} Versionen` },
  ];
  const reiterTaste = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const i = reiter.findIndex((r) => r.id === seite);
    const n = e.key === 'Home' ? 0 : e.key === 'End' ? reiter.length - 1
      : (i + (e.key === 'ArrowRight' ? 1 : -1) + reiter.length) % reiter.length;
    setSeite(reiter[n].id);
    document.getElementById(`${idBasis}-reiter-${reiter[n].id}`)?.focus();
  };

  // ── Darstellung ──────────────────────────────────────────────────────────

  const buehnenHtml = version ? version.html ?? '' : mail.html;
  const buehnenModus: BuehnenModus = version ? 'ansehen' : modus;

  const bearbeitenText = !adapter.darfEntscheiden
    ? 'Deine Änderungen werden als Vorschlag gespeichert — die Mail selbst ändert sich erst, wenn das Team sie übernimmt.'
    : mail.aus_assistent
      ? 'Diese Mail kommt aus dem Assistenten — Korrekturen am Inhalt bitte dort. Hier kannst Du Änderungen als Vorschlag festhalten.'
      : darfDirekt
        ? '„Direkt übernehmen“ speichert Deine Änderung sofort als neue Version. „Als Vorschlag speichern“ legt sie erst zur Abstimmung ab.'
        : 'Deine Änderungen werden als Vorschlag gespeichert. Direkt übernehmen dürfen Admins und wer die Mail angelegt hat.';

  const entfernenText = !adapter.darfEntscheiden
    ? 'Deine Auswahl wird als Vorschlag gespeichert — die Mail selbst ändert sich erst, wenn das Team sie übernimmt.'
    : mail.aus_assistent
      ? 'Diese Mail kommt aus dem Assistenten — Abschnitte bitte dort entfernen. Hier kannst Du sie als Vorschlag festhalten.'
      : darfDirekt
        ? '„Direkt entfernen“ nimmt die Abschnitte sofort aus der Mail (neue Version, im Versionsverlauf wieder abrufbar). „Als Vorschlag speichern“ legt sie erst zur Abstimmung ab.'
        : 'Deine Auswahl wird als Vorschlag gespeichert. Direkt entfernen dürfen Admins und wer die Mail angelegt hat.';

  const hinweis: Record<Exclude<BuehnenModus, 'bearbeiten' | 'entfernen'>, string> = {
    ansehen: 'So kommt die Mail an. Links öffnen sich in einem neuen Tab.',
    kommentieren: 'Markiere Text oder tipp auf eine Stelle — dann „Kommentieren“.',
    zeichnen: 'Kreis eine Stelle ein oder streich etwas an. Danach „Fertig“ und kurz dazuschreiben, was gemeint ist.',
    aenderungen: zahlVorschlaege
      ? 'Rot gestrichen fällt weg, grün ist neu. Tipp auf eine Stelle, um den Vorschlag zu sehen.'
      : 'Gerade gibt es keine offenen Änderungsvorschläge.',
  };
  const HinweisIcon = MODI.find((m) => m.id === modus)?.Icon ?? Info;

  return (
    <div
      ref={wurzelRef}
      className={`za-werkstatt${breit ? ' za-werkstatt--breit' : ''}`}
      style={{ '--za-luecke': `${LUECKE}px` } as React.CSSProperties}
    >
      {kopf}

      <div className="za-leiste" role="toolbar" aria-label="Werkzeuge der Mail-Werkstatt">
        <div className="za-modi" role="group" aria-label="Modus">
          {modi.map(({ id, label, Icon, tipp }) => {
            const gesperrt = (id === 'bearbeiten' || id === 'entfernen') && versendet;
            const an = modus === id && !version;
            return (
              <button
                key={id}
                type="button"
                className={`za-modus${an ? ' za-modus--an' : ''}`}
                aria-pressed={an}
                aria-label={id === 'aenderungen' && zahlVorschlaege ? `${label} (${zahlVorschlaege} offen)` : label}
                title={gesperrt ? 'Die Mail ist schon versendet — sie lässt sich nicht mehr ändern.' : tipp}
                disabled={gesperrt}
                onClick={() => modusWechseln(id)}
              >
                <Icon size={16} aria-hidden="true" />
                <span className="za-modus-text">{label}</span>
                {id === 'aenderungen' && zahlVorschlaege > 0 && <span className="za-modus-zahl" aria-hidden="true">{zahlVorschlaege}</span>}
              </button>
            );
          })}
        </div>
        {darfDirekt && (
          <div className="za-breiten" role="group" aria-label="Bild">
            <button
              type="button"
              className="za-breite"
              title={ungespeichert ? 'Speichere oder verwirf erst Deine Änderung.' : 'Ein Bild der Mail gegen ein neues Foto tauschen'}
              disabled={ungespeichert || !!version}
              onClick={() => setBildTausch(true)}
            >
              <ImagePlus size={16} aria-hidden="true" /><span className="za-modus-text">Bild tauschen</span>
            </button>
          </div>
        )}
        {adapter.istAdmin && (
          <div className="za-breiten" role="group" aria-label="Claude Code">
            <button
              type="button"
              className="za-breite"
              title="Aus den Rückmeldungen dieser Mail einen Auftrag für Claude Code erstellen"
              onClick={() => setClaudeOffen(true)}
            >
              <Terminal size={16} aria-hidden="true" /><span className="za-modus-text">Prompt für Claude Code</span>
            </button>
          </div>
        )}
        <div className="za-breiten" role="group" aria-label="Ansicht">
          <button type="button" className={`za-breite${breite === 'computer' ? ' za-breite--an' : ''}`} aria-pressed={breite === 'computer'} aria-label="Computer-Ansicht" title="Computer-Ansicht" onClick={() => setBreite('computer')}>
            <Monitor size={16} aria-hidden="true" /><span className="za-modus-text">Computer</span>
          </button>
          <button type="button" className={`za-breite${breite === 'handy' ? ' za-breite--an' : ''}`} aria-pressed={breite === 'handy'} aria-label="Handy-Ansicht" title="Handy-Ansicht" onClick={() => setBreite('handy')}>
            <Smartphone size={16} aria-hidden="true" /><span className="za-modus-text">Handy</span>
          </button>
        </div>
      </div>

      <div className="za-raster">
        <section className="za-hauptspalte" aria-label="Mail">
          {version ? (
            <div className="za-banner za-banner--version" role="status">
              <History size={16} aria-hidden="true" />
              <span>Du siehst Version {version.nr} vom {datumZeit(version.created_at)} — nur ansehen.</span>
              <button type="button" className="btn btn-sm" onClick={() => setVersion(null)}>
                <ArrowLeft size={14} aria-hidden="true" /> Zurück zur aktuellen Fassung
              </button>
            </div>
          ) : modus === 'bearbeiten' ? (
            <div className="za-bearbeiten">
              <div className="za-banner za-banner--bearbeiten">
                <Type size={16} aria-hidden="true" />
                <span>{bearbeitenText}</span>
              </div>
              <div className="za-bearbeiten-felder">
                <label className="za-feldgruppe">
                  <span className="za-feldgruppe-label">Betreff</span>
                  <input className="za-eingabe" value={betreff} onChange={(e) => setBetreff(e.target.value)} maxLength={300} />
                </label>
                <label className="za-feldgruppe">
                  <span className="za-feldgruppe-label">Kurz erklären (optional)</span>
                  <input
                    className="za-eingabe"
                    value={notiz}
                    onChange={(e) => setNotiz(e.target.value)}
                    maxLength={4000}
                    placeholder="z. B. Uhrzeit korrigiert"
                    aria-invalid={notizZuLang || undefined}
                  />
                  {notizZuLang && (
                    <span className="za-feld-hinweis" role="status">
                      {notiz.trim().length} Zeichen — „Direkt übernehmen“ nimmt höchstens {NOTIZ_MAX}. Als Vorschlag geht es.
                    </span>
                  )}
                </label>
              </div>
            </div>
          ) : modus === 'entfernen' ? (
            <div className="za-bearbeiten">
              <div className="za-banner za-banner--entfernen">
                <Trash2 size={16} aria-hidden="true" />
                <span>{entfernenText}</span>
              </div>
              <div className="za-bearbeiten-felder za-bearbeiten-felder--eins">
                <label className="za-feldgruppe">
                  <span className="za-feldgruppe-label">Kurz erklären (optional)</span>
                  <input
                    className="za-eingabe"
                    value={notiz}
                    onChange={(e) => setNotiz(e.target.value)}
                    maxLength={4000}
                    placeholder="z. B. Preisbox fällt weg, steht schon im Text"
                    aria-invalid={notizZuLang || undefined}
                  />
                  {notizZuLang && (
                    <span className="za-feld-hinweis" role="status">
                      {notiz.trim().length} Zeichen — „Direkt entfernen“ nimmt höchstens {NOTIZ_MAX}. Als Vorschlag geht es.
                    </span>
                  )}
                </label>
              </div>
            </div>
          ) : (
            <p className="za-modus-hinweis">
              <HinweisIcon size={14} aria-hidden="true" />
              {hinweis[modus]}
            </p>
          )}

          <div ref={bezugRef} className="za-buehnen-rahmen">
            {buehnenHtml.trim() ? (
              <MailBuehne
                ref={buehneRef}
                html={buehnenHtml}
                modus={buehnenModus}
                breite={breite}
                titel={`Mail: ${mail.betreff || mail.name}`}
                eintraege={version ? [] : buehnenEintraege}
                vorschlaege={version ? [] : buehnenVorschlaege}
                hervorgehoben={hoverId ?? aktivId}
                entwurf={version ? null : entwurf}
                onWaehlen={waehlen}
                onAnker={beimAnker}
                onMarkierung={beimMarkieren}
                onZeichnenAbbrechen={() => modusSetzen(vorherModus.current)}
                onPositionen={setPositionen}
                onBearbeitet={setBearbeitet}
                onStriche={setStriche}
                onAbschnitte={setAbschnitte}
                onMarkierungOhneOrt={setOhneOrt}
                onEscape={() => verlassenRef.current()}
              />
            ) : (
              <div className="za-leer za-leer--buehne">
                <Mail size={26} aria-hidden="true" />
                <p className="za-leer-titel">Diese Mail hat noch keinen Inhalt</p>
                <p className="za-leer-text">Sobald Text und Gestaltung stehen, erscheint die Mail hier.</p>
              </div>
            )}
          </div>

          {modus === 'bearbeiten' && !version && (
            <div className="za-aktionsleiste" role="region" aria-label="Änderungen speichern">
              <span className="za-aktionsleiste-stand" aria-live="polite">
                {hatAenderung
                  ? `${bearbeitet ? stellenText(bearbeitet) : ''}${bearbeitet && betreffGeaendert ? ' und ' : ''}${betreffGeaendert ? 'Betreff' : ''} geändert`
                  : 'Klick in einen Text und schreib los — markierter Text lässt sich über die Leiste fett, farbig oder als Link setzen.'}
              </span>
              <div className="za-aktionsleiste-knoepfe">
                <button type="button" className="btn btn-ghost btn-sm" onClick={verwerfen} disabled={speichert !== null}>Verwerfen</button>
                <button
                  type="button"
                  className={`btn btn-sm ${darfDirekt ? '' : 'btn-primary'}`}
                  onClick={() => { void alsVorschlagSpeichern(); }}
                  disabled={!hatAenderung || speichert !== null}
                >
                  {speichert === 'vorschlag' && <span className="spinner za-spinner-klein" aria-hidden="true" />}
                  Als Vorschlag speichern{bearbeitet ? ` (${stellenText(bearbeitet)})` : ''}
                </button>
                {darfDirekt && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => { void direktUebernehmen(); }}
                    disabled={!hatAenderung || speichert !== null || notizZuLang}
                  >
                    {speichert === 'direkt' ? <span className="spinner za-spinner-klein" aria-hidden="true" /> : <Check size={14} aria-hidden="true" />}
                    Direkt übernehmen
                  </button>
                )}
              </div>
            </div>
          )}
          {modus === 'entfernen' && !version && (
            <div className="za-aktionsleiste" role="region" aria-label="Abschnitte entfernen">
              <span className="za-aktionsleiste-stand" aria-live="polite">
                {abschnitte ? `${abschnittText(abschnitte)} zum Entfernen markiert` : 'Tippe in der Mail auf das, was weg soll.'}
              </span>
              <div className="za-aktionsleiste-knoepfe">
                <button type="button" className="btn btn-ghost btn-sm" onClick={verwerfen} disabled={speichert !== null}>Verwerfen</button>
                <button
                  type="button"
                  className={`btn btn-sm ${darfDirekt ? '' : 'btn-primary'}`}
                  onClick={() => { void entfernenAlsVorschlag(); }}
                  disabled={!abschnitte || speichert !== null}
                >
                  {speichert === 'vorschlag' && <span className="spinner za-spinner-klein" aria-hidden="true" />}
                  Als Vorschlag speichern{abschnitte ? ` (${abschnitte})` : ''}
                </button>
                {darfDirekt && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => { void direktEntfernen(); }}
                    disabled={!abschnitte || speichert !== null || notizZuLang}
                  >
                    {speichert === 'direkt' ? <span className="spinner za-spinner-klein" aria-hidden="true" /> : <Trash2 size={14} aria-hidden="true" />}
                    Direkt entfernen
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        <aside className={`za-seite za-seite--${seite}`} aria-label="Rückmeldungen und Versionen">
          <div className="za-reiter" role="tablist" aria-label="Seitenleiste" onKeyDown={reiterTaste}>
            {reiter.map(({ id, label, zahl, titel }) => (
              <button
                key={id}
                id={`${idBasis}-reiter-${id}`}
                type="button"
                role="tab"
                aria-selected={seite === id}
                aria-controls={`${idBasis}-panel`}
                tabIndex={seite === id ? 0 : -1}
                className={`za-reiter-knopf${seite === id ? ' za-reiter-knopf--an' : ''}`}
                onClick={() => setSeite(id)}
                title={titel}
              >
                <span>{label}</span>
                {zahl > 0 && <span className="za-zahl">{zahl}</span>}
              </button>
            ))}
          </div>
          <div id={`${idBasis}-panel`} role="tabpanel" aria-labelledby={`${idBasis}-reiter-${seite}`} className="za-reiter-inhalt">
            {seite === 'kommentare' && (
              <KommentarSpalte
                mail={mail}
                adapter={adapter}
                aktionen={aktionen}
                breite={breite}
                ausgerichtet={breit && !version}
                luecke={LUECKE}
                positionen={positionen}
                bezugRef={bezugRef}
                aktivId={aktivId}
                onWaehlen={waehlen}
                onHover={setHoverId}
                entwurf={entwurf}
                onEntwurfVerwerfen={() => setEntwurf(null)}
                onKommentiert={(id) => { setEntwurf(null); setAktivId(id); }}
                onBreite={setBreite}
                onZeigen={zeigen}
                onStelleWaehlen={!darfSchreiben || modus === 'kommentieren' ? undefined : () => modusWechseln('kommentieren')}
                fokus={fokus}
                text={kommentarText}
                onText={setKommentarText}
                markierungOhneOrt={version ? LEER : ohneOrt}
              />
            )}
            {seite === 'aenderungen' && (
              <AenderungenListe
                mail={mail}
                adapter={adapter}
                aktionen={aktionen}
                aktivId={aktivId}
                onWaehlen={waehlen}
                onZeigen={modus === 'aenderungen' ? undefined : () => modusWechseln('aenderungen')}
                onBearbeiten={versendet || !darfSchreiben ? undefined : () => modusWechseln('bearbeiten')}
              />
            )}
            {seite === 'versionen' && (
              <VersionenPanel
                mail={mail}
                adapter={adapter}
                gezeigtNr={version?.nr ?? null}
                onAnsehen={versionAnsehen}
                ungespeichert={textUngespeichert}
                onVerwerfen={() => modusSetzen(vorherModus.current)}
              />
            )}
          </div>
        </aside>
      </div>

      {bildTausch && darfDirekt && (
        <BildTauschen html={mail.html} onUebernehmen={bildUebernehmen} onSchliessen={() => setBildTausch(false)} />
      )}

      {claudeOffen && adapter.istAdmin && (
        <ClaudePromptDialog mail={mail} ichName={adapter.ichName} offen onClose={() => setClaudeOffen(false)} />
      )}

      <Rueckfrage
        offen={frage !== null}
        titel={frage?.titel ?? ''}
        text={frage?.text ?? ''}
        ja={frage?.ja ?? 'OK'}
        nein={frage?.nein}
        gefaehrlich={frage?.gefaehrlich}
        laeuft={frageLaeuft}
        onJa={() => { void frageBestaetigen(true); }}
        onNein={() => { void frageBestaetigen(false); }}
        onSchliessen={() => setFrage(null)}
      />
    </div>
  );
}
